/**
 * TopBar — 48px persistent top bar for authenticated routes.
 * Contains: breadcrumb, global search (⌘K), new scan, notifications, avatar dropdown.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Zap, X, Clock, User, Settings, Users, LogOut, Radio, UserCircle, Package, Megaphone, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useArsenalModal } from "@/components/ArsenalModal";
import { FoxApisModal } from "@/components/FoxApisModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { DenSwitcherPill } from "@/components/header/DenSwitcherPill";
import { CreditGauge } from "@/components/billing/CreditGauge";
import { WhatsNewPanel } from "@/components/layout/WhatsNewPanel";
import { AdminViewToggle } from "@/components/layout/AdminViewToggle";
import { ViewAsTierSelect } from "@/components/layout/ViewAsContext";
import type { Tables } from "@/integrations/supabase/types";

type Profile = Tables<"profiles">;

interface TopBarProps {
  profile: Profile | null;
}

// ── Scan Activity Indicator ──
function useScanActivity(userId: string | undefined) {
  const [isRunning, setIsRunning] = useState(false);
  useEffect(() => {
    if (!userId) return;
    const check = async () => {
      const { count } = await supabase
        .from("scan_history")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("status", "running");
      setIsRunning((count || 0) > 0);
    };
    check();
    const channel = supabase
      .channel("topbar-scan-status")
      .on("postgres_changes", { event: "*", schema: "public", table: "scan_history", filter: `user_id=eq.${userId}` }, check)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId]);
  return isRunning;
}

// ── Notifications Hook ──
function useNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(20);
      setNotifications(data || []);
      setUnreadCount((data || []).filter((n: any) => !n.read).length);
    };
    load();
    const channel = supabase
      .channel("topbar-notifications")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  const markAllRead = async () => {
    if (!userId) return;
    await supabase.from("notifications").update({ read: true }).eq("user_id", userId).eq("read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const markRead = async (id: string) => {
    await supabase.from("notifications").update({ read: true }).eq("id", id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  return { notifications, unreadCount, markAllRead, markRead };
}

// ── Global Search Hook ──
// SITE-SEARCH v0.01 (2026-05-29) — replaces the unified-search edge fn
// roundtrip with client-side fuse.js over a hand-curated SITE_SEARCH_INDEX.
// Three benefits:
//   1. No edge fn slot consumed; resilient if the fn is paused.
//   2. Forgiving fuzzy matching — "brandkit", "brad kite", "brand kit" all
//      land on Brand Kit. Includes aliases + tabs + headings as fields.
//   3. Sub-50ms response on every keystroke (no network roundtrip).
// Dashboard quick-jump groups (People / Brands / Scans) still query the DB
// since those are user-scoped data, not static routes.
import Fuse from "fuse.js";
import { SITE_SEARCH_INDEX, type SiteSearchEntry } from "@/config/site-search-index";
import { getMyTier } from "@/lib/userTier";

const SITE_FUSE = new Fuse<SiteSearchEntry>(SITE_SEARCH_INDEX, {
  keys: [
    { name: "title", weight: 1.0 },
    { name: "aliases", weight: 0.9 },
    { name: "tabs", weight: 0.7 },
    { name: "headings", weight: 0.6 },
    { name: "description", weight: 0.3 },
  ],
  threshold: 0.4,
  includeScore: true,
  minMatchCharLength: 2,
  ignoreLocation: true,
});

function logSearchClick(userId: string | null, query: string, resultId: string): void {
  // Best-effort telemetry. site_search_clicks table is created by the
  // SITE-SEARCH v0.01 migration. Failures here are silent — the user
  // navigation must NEVER block on telemetry.
  if (!userId) return;
  void (supabase.from("site_search_clicks" as any) as any).insert({
    user_id: userId,
    query: String(query || "").slice(0, 200),
    result_id: String(resultId || "").slice(0, 80),
  }).then(() => undefined, () => undefined);
}

function useGlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{ group: string; items: { id: string; primary: string; secondary: string; href: string }[] }[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        // Run fuse.js synchronously — pure in-memory, sub-1ms.
        const fuseHits = SITE_FUSE.search(query, { limit: 7 })
          .sort((a, b) => {
            // Lower score = better. We adjust by the entry's weight so that
            // the same fuse score on a Dashboard-core entry beats a Marketing
            // entry. Weights are roughly Dashboard 100 / INVEST 90 / Setup
            // 70-80 / Marketing 40-60 so the division produces a meaningful
            // tiebreaker without overwhelming the fuse score itself.
            const aScore = (a.score ?? 0) * (100 / (a.item.weight || 1));
            const bScore = (b.score ?? 0) * (100 / (b.item.weight || 1));
            return aScore - bScore;
          });
        const pageGroup = fuseHits.length
          ? [{
              group: "Pages",
              items: fuseHits.map((h) => ({
                id: h.item.id,
                primary: h.item.title,
                secondary: `${h.item.section} · ${h.item.description}`,
                href: h.item.url,
              })),
            }]
          : [];

        // User-scoped data still hits the DB (Contacts / Brands / Scans).
        const [contacts, brands, scans] = await Promise.all([
          supabase.from("contacts").select("id, full_name, employer").ilike("full_name", `%${query}%`).limit(3),
          supabase.from("brands").select("id, brand_name").ilike("brand_name", `%${query}%`).limit(3),
          supabase.from("scan_history").select("id, primary_term").ilike("primary_term", `%${query}%`).limit(3),
        ]);
        const groups: typeof results = [...pageGroup];
        if (contacts.data?.length) groups.push({ group: "People", items: contacts.data.map((c) => ({ id: c.id, primary: c.full_name, secondary: c.employer || "", href: `/dashboard/contacts` })) });
        if (brands.data?.length) groups.push({ group: "Brands", items: brands.data.map((b) => ({ id: b.id, primary: b.brand_name, secondary: "", href: `/brands/${b.id}` })) });
        if (scans.data?.length) groups.push({ group: "Scans", items: scans.data.map((s) => ({ id: s.id, primary: s.primary_term || "Scan", secondary: "", href: `/dashboard/scans/${s.id}` })) });
        setResults(groups);
      } catch { setResults([]); }
      setSearching(false);
    }, 150);
    return () => clearTimeout(timeout);
  }, [query]);

  return { query, setQuery, results, searching };
}

// ── Time Ago Formatter ──
function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const QUICK_JUMP = [
  { icon: "🎯", label: "Dealflow", href: "/dashboard/dealflow" },
  { icon: "📡", label: "Scans", href: "/dashboard/scans" },
  { icon: "👤", label: "Contacts", href: "/dashboard/contacts" },
  { icon: "⚡", label: "Sequences", href: "/dashboard/sequences" },
  { icon: "📦", label: "Content", href: "/dashboard/saved" },
];

const NOTIF_ICONS: Record<string, string> = {
  "high-intent": "🔥",
  "scan-complete": "✅",
  "comment-mention": "💬",
  "gift-viewed": "🎁",
  "sequence-alert": "⚡",
};

export function TopBar({ profile }: TopBarProps) {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumb();
  const scanRunning = useScanActivity(profile?.id);
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications(profile?.id);
  const { query, setQuery, results, searching } = useGlobalSearch();
  const { openArsenal } = useArsenalModal();

  const [searchFocused, setSearchFocused] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [foxApisOpen, setFoxApisOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [unreadChangelog, setUnreadChangelog] = useState(0);
  // The one tier column, read once. There is no tier on the profiles row any more.
  const [topBarTier, setTopBarTier] = useState("free");

  // Unread changelog count for the What's New button. Re-runs whenever
  // the panel closes so the badge resets after a read.
  useEffect(() => {
    if (!profile) return;
    let alive = true;
    const lastSeen: string | null = (profile as any).last_seen_changelog ?? null;
    (async () => {
      const tier = await getMyTier();
      setTopBarTier(tier);
      let query = (supabase as any).from("changelog_entries").select("visible_tiers").eq("is_published", true);
      if (lastSeen) query = query.gt("published_at", lastSeen);
      const { data: rows } = await query;
      if (!alive) return;
      const filtered = (rows || []).filter((e: any) => !e.visible_tiers?.length || e.visible_tiers.includes(tier));
      setUnreadChangelog(filtered.length);
    })();
    return () => { alive = false; };
  }, [profile, whatsNewOpen]);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);

  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("mf_recent_searches") || "[]"); } catch { return []; }
  });

  // ⌘K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") {
        setSearchFocused(false);
        setNotifOpen(false);
        setAvatarOpen(false);
        searchRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target as Node) && !searchRef.current?.contains(e.target as Node)) setSearchFocused(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) setAvatarOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);


  const handleSearch = useCallback((q: string) => {
    setQuery(q);
  }, [setQuery]);

  const handleSearchSubmit = () => {
    if (query.trim().length >= 2) {
      const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem("mf_recent_searches", JSON.stringify(updated));
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const userInitials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : profile?.email?.slice(0, 2).toUpperCase() || "U";

  return (
    <>
      <header
      className="flex items-center justify-between flex-shrink-0"
      style={{ height: 48, background: "#0a0d12", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "0 20px" }}
    >
      {/* ── LEFT: Breadcrumb ── */}
      <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
        <SidebarTrigger className="text-muted-foreground hover:text-foreground mr-1" />
        {scanRunning && (
          <span className="relative flex h-2 w-2 mr-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        )}
        {breadcrumbs.map((seg, i) => (
          <span key={i} className="flex items-center">
            {i > 0 && <span style={{ color: "rgba(255,255,255,0.2)", margin: "0 6px", fontSize: 12 }}>›</span>}
            {seg.href && i < breadcrumbs.length - 1 ? (
              <a
                href={seg.href}
                onClick={(e) => { e.preventDefault(); navigate(seg.href!); }}
                style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, textDecoration: "none", cursor: "pointer", transition: "color 0.15s" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.8)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
              >
                {seg.label}
              </a>
            ) : (
              <span style={{ color: i === breadcrumbs.length - 1 ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.4)", fontSize: 13, fontWeight: i === breadcrumbs.length - 1 ? 500 : 400 }}>
                {seg.label}
              </span>
            )}
          </span>
        ))}
      </div>

      {/* ── CENTER: Search ── */}
      <div className="relative hidden md:block" style={{ width: searchFocused ? 400 : 240, transition: "width 0.2s ease" }}>
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ width: 14, height: 14, color: "rgba(255,255,255,0.3)" }} />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSearchSubmit(); }}
          placeholder="Search... ⌘K"
          className="w-full outline-none"
          style={{
            height: 32,
            background: searchFocused ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.05)",
            border: `1px solid ${searchFocused ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)"}`,
            borderRadius: 6,
            padding: "0 12px 0 32px",
            color: "white",
            fontSize: 13,
            transition: "all 0.15s",
          }}
        />
        {/* Search Dropdown */}
        {searchFocused && (
          <div
            ref={searchDropdownRef}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "#131820",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              padding: "8px 0",
              zIndex: 100,
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            {/* Quick Jump */}
            {query.length < 2 && (
              <>
                <div style={{ padding: "6px 12px", fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" as const }}>Quick Jump</div>
                {QUICK_JUMP.map((item) => (
                  <button
                    key={item.href}
                    className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
                    style={{ padding: "8px 12px", fontSize: 13, color: "rgba(255,255,255,0.7)", height: 36, border: "none", background: "none", cursor: "pointer" }}
                    onClick={() => { navigate(item.href); setSearchFocused(false); setQuery(""); }}
                  >
                    <span>{item.icon}</span> {item.label}
                  </button>
                ))}
              </>
            )}

            {/* Recent Searches */}
            {query.length < 2 && recentSearches.length > 0 && (
              <>
                <div style={{ padding: "10px 12px 6px", fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" as const }}>Recent</div>
                {recentSearches.map((s) => (
                  <button
                    key={s}
                    className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
                    style={{ padding: "8px 12px", fontSize: 13, color: "rgba(255,255,255,0.5)", height: 36, border: "none", background: "none", cursor: "pointer" }}
                    onClick={() => { setQuery(s); handleSearch(s); }}
                  >
                    <Clock style={{ width: 12, height: 12 }} /> {s}
                  </button>
                ))}
              </>
            )}

            {/* Search Results */}
            {query.length >= 2 && results.length > 0 && results.map((group) => (
              <div key={group.group}>
                <div style={{ padding: "10px 12px 6px", fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" as const }}>{group.group}</div>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
                    style={{ padding: "8px 12px", fontSize: 13, color: "rgba(255,255,255,0.7)", height: 36, border: "none", background: "none", cursor: "pointer" }}
                    onClick={() => {
                      logSearchClick(profile?.id || null, query, item.id);
                      navigate(item.href);
                      setSearchFocused(false);
                      setQuery("");
                    }}
                    title={item.secondary || item.primary}
                  >
                    <span style={{ color: "rgba(255,255,255,0.7)" }}>{item.primary}</span>
                    {item.secondary && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{item.secondary}</span>}
                  </button>
                ))}
              </div>
            ))}

            {query.length >= 2 && results.length === 0 && !searching && (
              <div style={{ padding: "16px 12px", fontSize: 13, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
                No results for "{query}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── RIGHT: Actions ── */}
      <div className="flex items-center gap-3">
        {/* Enrichment credit gauge — compact, hidden on small screens. */}
        <div className="hidden lg:block">
          <CreditGauge compact />
        </div>
        <button
          onClick={() => setFoxApisOpen(true)}
          className="hidden md:flex items-center hover:text-white transition-colors"
          style={{
            height: 30,
            padding: "0 10px",
            fontSize: 12,
            fontWeight: 500,
            color: "rgba(255,255,255,0.65)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 6,
            background: "rgba(255,255,255,0.03)",
            cursor: "pointer",
          }}
        >
          APIs &amp; AI Agents
        </button>

        {/* "New Scan" header button removed 2026-07 — scans downplayed; scan
            creation moves into the Signals section as "New Signal" later. The
            underlying scan flow (/dashboard/scans?new=true) is unchanged. */}

        {/* What's New / changelog nav link hidden 2026-06-13 — MF changelog superseded by FoxTale.
            Gated with {false} (not deleted) so it never renders but keeps refs valid; reversible. */}
        {false && (
        <div className="relative hidden md:block">
          <button
            onClick={() => { setWhatsNewOpen(true); }}
            className="flex items-center gap-1.5 transition-colors hover:text-white"
            title={unreadChangelog > 0 ? `What's New (${unreadChangelog} unread)` : "What's New"}
            style={{
              height: 30, padding: "0 10px", fontSize: 12, fontWeight: 500,
              color: "rgba(255,255,255,0.65)", border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 6, background: "rgba(255,255,255,0.03)", cursor: "pointer",
            }}
          >
            <Megaphone style={{ width: 13, height: 13 }} /> What's New
            {unreadChangelog > 0 && (
              <span
                aria-hidden
                style={{
                  marginLeft: 2, width: 6, height: 6, borderRadius: 999,
                  background: "hsl(var(--primary))", display: "inline-block",
                }}
              />
            )}
          </button>
        </div>
        )}

        {/* Standalone "Den" switcher removed 2026-07 — merged into the den-name
            chip (DenSwitcherPill below): the chip itself opens the switch pulldown. */}

        {/* Notifications Bell */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(!notifOpen); setAvatarOpen(false); }}
            className="relative transition-colors"
            style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 4 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.9)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
          >
            <Bell style={{ width: 18, height: 18 }} />
            {unreadCount > 0 && (
              <span
                className="absolute flex items-center justify-center"
                style={{
                  top: 0, right: 0, minWidth: 16, height: 16, borderRadius: "50%",
                  background: "#ef4444", color: "white", fontSize: 9, fontWeight: 700,
                  transform: "translate(4px, -4px)",
                }}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, width: 320,
                background: "#131820", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                maxHeight: 400, overflowY: "auto", zIndex: 100,
              }}
            >
              <div className="flex items-center justify-between" style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ color: "white", fontSize: 13, fontWeight: 600 }}>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 11, cursor: "pointer" }}>
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div style={{ padding: "32px 16px", textAlign: "center" }}>
                  <Bell style={{ width: 32, height: 32, color: "rgba(255,255,255,0.15)", margin: "0 auto 12px" }} />
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>No notifications yet</p>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 4, lineHeight: 1.4 }}>
                    High-intent signals, scan completions, and team mentions will appear here.
                  </p>
                </div>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    className="w-full text-left hover:bg-white/5 transition-colors"
                    style={{
                      padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 10,
                      background: n.read ? "none" : "rgba(255,255,255,0.02)",
                      border: "none", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)",
                    }}
                    onClick={() => { markRead(n.id); if (n.link_url) navigate(n.link_url); setNotifOpen(false); }}
                  >
                    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}>{NOTIF_ICONS[n.type] || "📌"}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ fontSize: 12, color: n.read ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.85)", lineHeight: 1.3 }}>{n.title}</p>
                      {n.body && <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 2, lineHeight: 1.3 }}>{n.body}</p>}
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 3, display: "block" }}>{timeAgo(n.created_at)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Den Switcher Pill — sits between notifications and the avatar.
            Drives sidebar filtering via 'den-state-changed' CustomEvent. */}
        <DenSwitcherPill />

        {/* T779: admin-only plan preview (admin view on) and the "Admin view" switch,
            right next to the account menu. Both render nothing for non-admins. */}
        <ViewAsTierSelect />
        <AdminViewToggle />

        {/* Avatar */}
        <div className="relative" ref={avatarRef}>
          <button
            onClick={() => { setAvatarOpen(!avatarOpen); setNotifOpen(false); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <Avatar className="h-7 w-7" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
              {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
              <AvatarFallback className="bg-primary text-primary-foreground text-[10px]">{userInitials}</AvatarFallback>
            </Avatar>
          </button>

          {avatarOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, width: 200,
                background: "#131820", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                zIndex: 100, overflow: "hidden",
              }}
            >
              <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {profile?.full_name || "User"}
                </p>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {profile?.email}
                </p>
              </div>
              {[
                { icon: <User style={{ width: 14, height: 14 }} />, label: "My Profile", action: () => navigate("/settings/profile") },
                { icon: <Building2 style={{ width: 14, height: 14 }} />, label: "My Companies →", action: () => navigate("/dashboard/invest/overview#my-companies-section") },
                { icon: <Zap style={{ width: 14, height: 14 }} />, label: "My Arsenal", action: () => { openArsenal(); setAvatarOpen(false); } },
                { icon: <Users style={{ width: 14, height: 14 }} />, label: "My Team", action: () => navigate("/dashboard/my-team") },
                { icon: <Settings style={{ width: 14, height: 14 }} />, label: "Settings", action: () => navigate("/dashboard/settings") },
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
                  style={{ padding: "8px 14px", height: 36, border: "none", background: "none", cursor: "pointer", color: "rgba(255,255,255,0.7)", fontSize: 13 }}
                  onClick={() => { item.action(); setAvatarOpen(false); }}
                >
                  {item.icon} {item.label}
                </button>
              ))}
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <button
                  className="w-full text-left flex items-center gap-2 hover:bg-destructive/10 transition-colors"
                  style={{ padding: "8px 14px", height: 36, border: "none", background: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", fontSize: 13 }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                  onClick={handleSignOut}
                >
                  <LogOut style={{ width: 14, height: 14 }} /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </header>
      <FoxApisModal open={foxApisOpen} onOpenChange={setFoxApisOpen} />
      {profile && (
        <WhatsNewPanel
          open={whatsNewOpen}
          onOpenChange={(open) => {
            setWhatsNewOpen(open);
            if (!open) setUnreadChangelog(0);
          }}
          userTier={topBarTier}
          userId={profile.id}
        />
      )}
    </>
  );
}
